import { useState, useEffect, useCallback, useRef, memo, type KeyboardEvent } from 'react';
import { ArrowUp } from 'lucide-react';

const ScrollToTopButton = memo(() => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const announcementRef = useRef<HTMLDivElement>(null);
  
  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      const shouldShow = window.scrollY > 400;
      setShowScrollTop(shouldShow);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    setIsScrolling(true);
    
    // Announce scroll action to screen readers
    if (announcementRef.current) {
      announcementRef.current.textContent = 'Scrolling to top of page';
    }
    
    window.scrollTo({ 
      top: 0, 
      behavior: 'smooth' 
    });
    
    // Reset scrolling state after animation
    setTimeout(() => {
      setIsScrolling(false);
      if (announcementRef.current) {
        announcementRef.current.textContent = 'Reached top of page';
      }
    }, 1000);
  }, []);

  // Keyboard support
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      scrollToTop();
    }
  }, [scrollToTop]);

  if (!showScrollTop) {
    return (
      <div 
        ref={announcementRef} 
        className="sr-only" 
        aria-live="polite" 
        aria-atomic="true"
      />
    );
  }

  return (
    <>
      {/* Screen reader announcements */}
      <div 
        ref={announcementRef} 
        className="sr-only" 
        aria-live="polite" 
        aria-atomic="true"
      />
      
      <button
        onClick={scrollToTop}
        onKeyDown={handleKeyDown}
        className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg transition-transform duration-300 hover:scale-110 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:shadow-xl"
        aria-label={isScrolling ? 'Scrolling to top of page' : 'Scroll to top of page'}
        aria-describedby="scroll-to-top-description"
        disabled={isScrolling}
      >
        <ArrowUp 
          className={`h-6 w-6 transition-transform duration-300 ${
            isScrolling ? 'animate-bounce' : ''
          }`} 
          aria-hidden="true" 
        />
      </button>
      
      {/* Hidden description for screen readers */}
      <div id="scroll-to-top-description" className="sr-only">
        Scroll smoothly to the top of the page. This button appears when you have scrolled down more than 400 pixels.
      </div>
    </>
  );
});

ScrollToTopButton.displayName = 'ScrollToTopButton';

export default ScrollToTopButton;